<template>
  <div class="bg-white rounded-xl border border-red-200 p-6">
    <!-- Warning + action -->
    <div class="flex items-start gap-4">
      <!-- Warning icon -->
      <div class="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
        <svg class="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-red-700">{{ warningTitle }}</p>
        <p class="text-sm text-gray-500 mt-0.5">{{ warningText }}</p>

        <!-- Confirm checkbox -->
        <label v-if="requireConfirm" class="flex items-center gap-2.5 mt-3 cursor-pointer">
          <input type="checkbox" v-model="confirmed" class="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-500" />
          <span class="text-sm text-gray-600">{{ confirmLabel }}</span>
        </label>
      </div>

      <!-- Delete button -->
      <div class="shrink-0">
        <button
          @click="onDelete"
          :disabled="!!disabledMessage || (requireConfirm && !confirmed)"
          class="btn-danger"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
          </svg>
          {{ buttonLabel }}
        </button>
        <p v-if="disabledMessage" class="text-xs text-red-400 mt-1.5">{{ disabledMessage }}</p>
      </div>
    </div>
  </div>

  <!-- Confirm modal -->
  <ConfirmModal
    :show="showModal"
    :loading="deleting"
    title="Confirm Deletion"
    :message="confirmMessage"
    :confirm-text="buttonLabel"
    :danger="true"
    @confirm="executeDelete"
    @cancel="showModal = false"
  />
</template>

<script>
import ConfirmModal from './ConfirmModal.vue'
import api from '../services/api.js'
import { notifySuccess, notifyError } from '../services/notifications.js'

export default {
  name: 'DeleteResource',
  components: { ConfirmModal },
  emits: ['deleted'],

  props: {
    warningTitle: { type: String, default: 'You are about to delete this resource' },
    warningText: { type: String, default: 'This action cannot be undone. Please confirm before proceeding.' },
    buttonLabel: { type: String, default: 'Delete' },
    confirmLabel: { type: String, default: 'I confirm this deletion' },
    confirmMessage: { type: String, default: 'Are you sure you want to proceed with this action?' },
    successMessage: { type: String, default: 'Resource deleted successfully' },
    endpoint: { type: String, required: true },
    requireConfirm: { type: Boolean, default: true },
    disabledMessage: { type: String, default: '' }
  },

  data() {
    return {
      confirmed: false,
      showModal: false,
      deleting: false
    }
  },

  methods: {
    onDelete() {
      this.showModal = true
    },

    async executeDelete() {
      this.deleting = true
      try {
        await api.delete(this.endpoint)
        notifySuccess(this.successMessage)
        this.showModal = false
        this.$emit('deleted')
      } catch (err) {
        notifyError(err?.message || 'Failed to delete resource')
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
